import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, GitBranch, GraduationCap, Users, Loader2, ArrowUpRight } from 'lucide-react';
import { FilChronoListe } from '../entreprise/FilChronoDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ligne dépliable générique (candidats / fil chronologique)
export const LigneDepliable = ({ icon: Icon, titre, resume, droite, children, testId, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={testId}>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-slate-50 rounded-xl" data-testid={`${testId}-toggle`}>
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800 min-w-0"><Icon className="w-4 h-4 text-indigo-500 shrink-0" /> {titre} {resume}</span>
        <span className="flex items-center gap-3 text-xs text-slate-500 shrink-0">{droite}{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
      </button>
      {open && <div className="border-t px-5 py-4">{children}</div>}
    </div>
  );
};

export const FilChronoLigne = ({ action, dossierId }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || data) return;
    axios.get(`${API_URL}/portail/admin/dossier/${dossierId}/chronologie`).then(r => setData(r.data)).catch(() => setData({ journees: [] }));
  }, [open, data, dossierId]);
  const journees = data?.journees || [];
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="apercu-fil-chrono">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-slate-50 rounded-xl" data-testid="apercu-fil-chrono-toggle">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800"><GitBranch className="w-4 h-4 text-indigo-500" /> Fil chronologique</span>
        <span className="flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-2"><span>Avancement</span><span className="w-24 h-1.5 rounded-full bg-slate-100 overflow-hidden inline-block"><span className="block h-full bg-cyan-500" style={{ width: `${action.progression}%` }} /></span><span className="font-semibold text-slate-700" data-testid="apercu-fil-avancement">{action.jours_passes}/{action.total_jours} j · {action.progression}%</span></span>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {open && <div className="border-t px-5 py-4">{!data ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : <FilChronoListe journees={journees} />}</div>}
    </div>
  );
};

// Tuile AFT liée (sous l'AF mère) ou AF mère (sous une AFT)
export const TuileLiee = ({ aft, afMere, nbTotal, nbDelegues }) => {
  const navigate = useNavigate();
  if (aft) {
    return (
      <button type="button" onClick={() => navigate(`/dossiers/${aft.id}`)} className="w-full rounded-xl border border-cyan-200 bg-cyan-50/50 shadow-sm px-5 py-3 flex items-center justify-between gap-3 text-left hover:bg-cyan-50" data-testid={`apercu-aft-${aft.id}`}>
        <span className="flex items-center gap-3 min-w-0">
          <GraduationCap className="w-5 h-5 text-cyan-600 shrink-0" />
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-slate-900 truncate"><span className="font-mono text-xs text-cyan-700 mr-2">{aft.code}</span>{aft.intitule}</span>
            <span className="block text-xs text-slate-500">{aft.module ? `${aft.module.code} — ${aft.module.titre}` : 'Action de certification'}{aft.date_debut ? ` · ${aft.date_debut}${aft.date_fin && aft.date_fin !== aft.date_debut ? ` → ${aft.date_fin}` : ''}` : ''}</span>
          </span>
        </span>
        <span className="flex items-center gap-3 text-xs shrink-0">
          <span className="flex items-center gap-1 font-semibold text-cyan-700" data-testid={`apercu-aft-nb-${aft.id}`}><Users className="w-3.5 h-3.5" /> {aft.nb_candidats} délégué(s)</span>
          {aft.nb_jours > 1 && <span className="text-slate-500">{aft.nb_jours} jours</span>}
          {aft.nb_non_repartis > 0 && <span className="text-amber-700 font-semibold" data-testid={`apercu-aft-non-repartis-${aft.id}`}>{aft.nb_non_repartis} sans jour de passage</span>}
          {aft.nb_restants != null ? (
            <span className="flex items-center gap-2" data-testid={`apercu-aft-restants-${aft.id}`}><span className={aft.nb_certifies >= aft.nb_candidats && aft.nb_candidats > 0 ? 'text-emerald-700 font-semibold' : 'text-indigo-700 font-semibold'} data-testid={`apercu-aft-certifies-${aft.id}`}>{aft.nb_certifies ?? 0}/{aft.nb_candidats} certifié(s)</span><span className={aft.nb_restants > 0 ? 'text-amber-700' : 'text-emerald-700'}>{aft.nb_restants} reste(nt) à passer</span></span>
          ) : (
            <span className="text-slate-400">{Math.max(0, nbTotal - nbDelegues)} restant(s) sur l'AF</span>
          )}
          <ArrowUpRight className="w-4 h-4 text-cyan-600" />
        </span>
      </button>
    );
  }
  if (afMere) {
    return (
      <button type="button" onClick={() => navigate(`/dossiers/${afMere.id}`)} className="w-full rounded-xl border border-indigo-200 bg-indigo-50/50 shadow-sm px-5 py-3 flex items-center justify-between gap-3 text-left hover:bg-indigo-50" data-testid="apercu-af-mere">
        <span className="flex items-center gap-3 min-w-0"><ArrowUpRight className="w-5 h-5 text-indigo-600 rotate-180 shrink-0" /><span className="min-w-0"><span className="block text-sm font-semibold text-slate-900 truncate"><span className="font-mono text-xs text-indigo-700 mr-2">{afMere.code}</span>{afMere.intitule}</span><span className="block text-xs text-slate-500">AF mère — {afMere.nb_candidats} candidat(s) au total, {nbTotal} délégué(s) sur cette AFT</span></span></span>
      </button>
    );
  }
  return null;
};
